import { getAppStore, setAppStore } from '@/store';
import renameDriveFile from '@/utils/generation/renameDriveFile';
import { clearGeneration } from '@/utils/storage/generationStorage';

import type { BuiltFlowMedia } from '@/utils/generation/buildFlowMedia';

/**
 * Final stage of the auto-generation pipeline:
 *  1. rename the file on Drive so the name persists across refreshes
 *  2. update the loading placeholder with the real result data
 *  3. clear the persisted generation entry (recovery handles only mid-poll
 *     crashes — once we have a result, the entry is no longer needed)
 *  4. surface the result via the lightbox in result mode
 *
 * Auto-generation always lands on the Generations panel (the prepare stage
 * navigated there), so we unconditionally open the lightbox in result mode
 * — matching the behaviour of `decidePostGenerationUX` for the same screen
 * state without needing to re-read the store after navigation.
 */
const finalizeAutoGeneration = async ({
  generationId,
  placeholderUuid,
  presetTitle,
  built,
}: {
  generationId: string;
  placeholderUuid: string;
  presetTitle: string;
  built: BuiltFlowMedia;
}): Promise<void> => {
  const { resultFile, resultType, isVideo, isAudio } = built;
  const timestamp = Date.now();

  const { filename } = await renameDriveFile({
    driveUid: resultFile.driveUid,
    presetTitle,
    resultType,
    timestamp,
  });

  // Update placeholder with real result
  getAppStore().updateContentFile({
    uuid: placeholderUuid,
    updates: {
      uuid: resultFile.driveUid || placeholderUuid,
      url: resultFile.url,
      name: filename,
      type: resultFile.type,
      preview: {
        url: isVideo ? resultFile.poster : isAudio ? undefined : resultFile.url,
        width: resultFile.width,
        height: resultFile.height,
      },
      isLoading: false,
    },
  });

  await clearGeneration(generationId);

  setAppStore({
    resultFile,
    generationTimestamp: timestamp,
    pendingLightboxFileId: resultFile.driveUid || placeholderUuid,
    isResultMode: true,
  });
};

export default finalizeAutoGeneration;
